/**
 * ReversrPanel — Edit history viewer with diff preview.
 *
 * Composes:
 * - reversr-ui (useReversr): Undo/redo history management
 * - diffr-lib (conceptual): Diff computation for preview
 * - widgetr-lib (Button, Badge): Action buttons, indicators
 *
 * Shows a chronological list of edits distinguishing:
 * - Manual edits (user typing)
 * - AI edits (synthesis, rewrite, expand, etc.)
 *
 * Users can cycle through edits and preview the diff
 * (before → after) for each change.
 */

import { useState, useMemo, useCallback } from 'react';
import { useReversr } from '@opcentral/reversr-ui';
import { useExpressrContext } from '../context/ExpressrContext.js';

/** Typed edit history entry stored via reversr recordChange */
export interface EditHistoryEntry {
  before: string;
  after: string;
  source: 'manual' | 'ai';
  description: string;
  timestamp: number;
  /** Which preset action or custom prompt was used */
  action?: string;
}

/** Compute a simple line-level diff for preview */
function computeLineDiff(before: string, after: string): Array<{ type: 'same' | 'add' | 'remove'; text: string }> {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const result: Array<{ type: 'same' | 'add' | 'remove'; text: string }> = [];

  // Simple LCS-based diff for reasonable-sized documents
  const maxLen = Math.max(beforeLines.length, afterLines.length);
  let bi = 0;
  let ai = 0;

  while (bi < beforeLines.length || ai < afterLines.length) {
    if (bi < beforeLines.length && ai < afterLines.length && beforeLines[bi] === afterLines[ai]) {
      result.push({ type: 'same', text: beforeLines[bi]! });
      bi++;
      ai++;
    } else {
      // Look ahead to find next match
      let foundInAfter = -1;
      let foundInBefore = -1;
      for (let look = 1; look <= Math.min(5, maxLen); look++) {
        if (foundInAfter === -1 && ai + look < afterLines.length && beforeLines[bi] === afterLines[ai + look]) {
          foundInAfter = look;
        }
        if (foundInBefore === -1 && bi + look < beforeLines.length && beforeLines[bi + look] === afterLines[ai]) {
          foundInBefore = look;
        }
      }

      if (foundInAfter !== -1 && (foundInBefore === -1 || foundInAfter <= foundInBefore)) {
        // Lines were added
        for (let k = 0; k < foundInAfter; k++) {
          result.push({ type: 'add', text: afterLines[ai + k]! });
        }
        ai += foundInAfter;
      } else if (foundInBefore !== -1) {
        // Lines were removed
        for (let k = 0; k < foundInBefore; k++) {
          result.push({ type: 'remove', text: beforeLines[bi + k]! });
        }
        bi += foundInBefore;
      } else {
        // Changed line
        if (bi < beforeLines.length) {
          result.push({ type: 'remove', text: beforeLines[bi]! });
          bi++;
        }
        if (ai < afterLines.length) {
          result.push({ type: 'add', text: afterLines[ai]! });
          ai++;
        }
      }
    }
  }

  return result;
}

export function ReversrPanel() {
  const { history, currentIndex, canUndo, canRedo, undo, redo } = useReversr();
  const { activeDocument, updateContent } = useExpressrContext();
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Type-cast history entries
  const entries = history as EditHistoryEntry[];

  // Compute diff for preview
  const previewDiff = useMemo(() => {
    if (previewIndex === null || !entries[previewIndex]) return null;
    const entry = entries[previewIndex];
    return computeLineDiff(entry.before, entry.after);
  }, [previewIndex, entries]);

  const previewEntry = previewIndex !== null ? entries[previewIndex] : null;

  // Navigate to a specific edit state
  const goToEdit = useCallback((index: number) => {
    if (!activeDocument || index < 0 || index >= entries.length) return;
    // Apply the content from that edit's "after" state
    updateContent(entries[index]!.after);
  }, [activeDocument, entries, updateContent]);

  const handleUndo = useCallback(() => {
    if (!canUndo || currentIndex < 1) return;
    const entry = entries[currentIndex];
    if (entry) {
      updateContent(entry.before);
    }
    undo();
  }, [canUndo, currentIndex, entries, updateContent, undo]);

  const handleRedo = useCallback(() => {
    if (!canRedo || currentIndex >= entries.length - 1) return;
    const nextEntry = entries[currentIndex + 1];
    if (nextEntry) {
      updateContent(nextEntry.after);
    }
    redo();
  }, [canRedo, currentIndex, entries, updateContent, redo]);

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: 6, opacity: 0.6,
  };

  if (!activeDocument) {
    return (
      <div style={{ padding: 16, fontSize: 12, opacity: 0.6, height: '100%' }}>
        Select a document to view edit history.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Edit History</span>
        <div style={{ fontSize: 11, marginTop: 2, opacity: 0.6 }}>
          {entries.length} edits | Position {currentIndex + 1}/{entries.length}
        </div>
      </div>

      {/* Undo/Redo controls */}
      <div style={{ padding: '8px 16px', display: 'flex', gap: 8, borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <button
          onClick={handleUndo}
          disabled={!canUndo}
          style={{ flex: 1, padding: '6px 10px', opacity: canUndo ? 1 : 0.4 }}
        >
          Undo
        </button>
        <button
          onClick={handleRedo}
          disabled={!canRedo}
          style={{ flex: 1, padding: '6px 10px', opacity: canRedo ? 1 : 0.4 }}
        >
          Redo
        </button>
      </div>

      {/* Edit list */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {entries.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, opacity: 0.5, textAlign: 'center' }}>
            No edits recorded yet. Start editing to see history.
          </div>
        ) : (
          <div style={{ padding: '4px 0' }}>
            {entries.map((entry, idx) => {
              const isCurrent = idx === currentIndex;
              const isAI = entry.source === 'ai';

              return (
                <div
                  key={idx}
                  onClick={() => setPreviewIndex(previewIndex === idx ? null : idx)}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    background: isCurrent ? 'rgba(108, 108, 240, 0.1)' : previewIndex === idx ? 'rgba(255,255,255,0.03)' : 'transparent',
                    borderLeft: isCurrent ? '3px solid #6c6cf0' : '3px solid transparent',
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Source indicator */}
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                      background: isAI ? 'rgba(139, 92, 246, 0.25)' : 'rgba(59, 130, 246, 0.25)',
                      color: isAI ? '#b794f4' : '#63b3ed',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      flexShrink: 0,
                    }}>
                      {isAI ? 'AI' : 'Edit'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.description}
                    </span>
                    <span style={{ fontSize: 10, opacity: 0.4, flexShrink: 0 }}>
                      {formatTime(entry.timestamp)}
                    </span>
                  </div>
                  {entry.action && (
                    <div style={{ fontSize: 10, opacity: 0.5, marginTop: 2, marginLeft: 39 }}>
                      {entry.action}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Diff preview */}
      {previewEntry && previewDiff && (
        <div style={{ borderTop: '1px solid var(--border, #2a2a4a)', maxHeight: '40%', overflow: 'auto' }}>
          <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={labelStyle}>Diff Preview</span>
            <button
              onClick={() => goToEdit(previewIndex!)}
              style={{ fontSize: 11, padding: '3px 8px' }}
            >
              Restore this version
            </button>
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontSize: 11,
            lineHeight: 1.6,
            padding: '0 16px 12px',
          }}>
            {previewDiff.slice(0, 50).map((line, i) => (
              <div
                key={i}
                style={{
                  padding: '0 4px',
                  background: line.type === 'add' ? 'rgba(72, 187, 120, 0.12)'
                    : line.type === 'remove' ? 'rgba(245, 101, 101, 0.12)'
                    : 'transparent',
                  color: line.type === 'add' ? '#68d391'
                    : line.type === 'remove' ? '#fc8181'
                    : 'inherit',
                  opacity: line.type === 'same' ? 0.4 : 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <span style={{ display: 'inline-block', width: 14, opacity: 0.5 }}>
                  {line.type === 'add' ? '+' : line.type === 'remove' ? '-' : ' '}
                </span>
                {line.text || '\u00A0'}
              </div>
            ))}
            {previewDiff.length > 50 && (
              <div style={{ opacity: 0.4, padding: '4px', textAlign: 'center' }}>
                ... {previewDiff.length - 50} more lines
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info footer */}
      <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border, #2a2a4a)', fontSize: 10, opacity: 0.4 }}>
        Click an edit to preview diff | Undo/Redo to cycle | Powered by reversr-ui
      </div>
    </div>
  );
}
